<template>
  <Teleport v-if="teleportElement" :to="teleportElement">
    <form class="modal" :class="[width, { compact }]" @submit="submit">
      <div class="header">
        <div class="text-container">
          <template v-if="texts.headerPrimary || texts.headerSecondary">
            <span class="primary-title">{{ $t(texts.headerPrimary) }}</span>
            <span v-if="texts.headerSecondary" class="secondary-title">{{ texts.headerSecondary }}</span>
          </template>
          <slot v-else name="header" />
        </div>
        <span class="close-btn" v-if="showCloseCorner" @click="close">X</span>
      </div>
      <div class="seperator" />
      <div class="main-content">
        <slot></slot>
      </div>
      <div class="footer">
        <slot name="footer"></slot>
        <div>
          <Button :isClean="true" @click="close" v-if="showCancelBtn">{{ texts.footerCancel ? $t(texts.footerCancel) : $t('cancel') }} </Button>
          <Button :isClean="true" @click="submit" v-if="showConfirmBtn" class="confirm" skin="primary" :disabled="isConfirmDisabled">{{ texts.footerConfirm ? $t(texts.footerConfirm) : $t('OK') }} </Button>
        </div>
      </div>
      <button type="submit" hidden />
    </form>
  </Teleport>
</template>

<script>
// Needs to be rendered outside tabs from material
import Button from './Button'
import Teleport from 'vue2-teleport'

export default {
  props: {
    width: {
      type: String,
      validator: (prop) => prop === 'small' || prop === 'medium' || prop === 'large',
      required: true,
    },
    showScreen: {
      type: Boolean,
      default: true,
    },
    texts: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    showCancelBtn: {
      type: Boolean,
      default: true,
    },
    showConfirmBtn: {
      type: Boolean,
      default: true,
    },
    showCloseCorner: {
      type: Boolean,
      default: true,
    },
    isConfirmDisabled: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    parent: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      teleportElement: null,
    }
  },
  created() {
    if (this.showScreen) {
      this.$store.dispatch({ type: 'setScreenMode', isVisible: true })
    }
  },
  mounted() {
    this.teleportElement = this.parent === 'shift' ? '.main' : `.${this.$el.parentElement.className}`
    this._previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  },
  destroyed() {
    document.body.style.overflow = this._previousBodyOverflow || ''
    this.$store.dispatch({ type: 'setScreenMode', isVisible: false })
  },
  methods: {
    submit(ev) {
      ev.preventDefault()
      this.$emit('submit')
    },
    close() {
      this.$emit('close')
      this.$store.dispatch({ type: 'setScreenMode', isVisible: false })
    },
  },
  components: {
    Button,
    Teleport,
  },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.modal {
  display: flex;
  flex-direction: column;
  position: fixed;
  z-index: 9999;
  background-color: $white;
  padding-bottom: 50px;
  border-radius: 10px;
  max-height: 90vh;
  overflow: hidden;

  .seperator {
    border-bottom: 1px solid #eaeaea;
  }

  &.small {
    min-width: 18vw;
    max-width: 50vw;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }

  &.medium {
    min-width: 32vw;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &.large {
    min-width: 60vw;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .header {
    display: flex;
    min-height: 100px;
    align-items: center;
    justify-content: space-between;

    .text-container {
      & > span {
        margin-inline-start: 40px;
      }

      .primary-title {
        font: Bold 24px/28px Ubuntu;
        letter-spacing: -0.27px;
        color: $black-blue;
      }

      .secondary-title {
        font: Normal 24px/28px Ubuntu;
        letter-spacing: -0.27px;
        color: $black-blue;
        margin-inline-start: 8px;
      }
    }

    .close-btn {
      cursor: pointer;
      margin-inline-end: 20px;
      transform: scaleX(1.7) scaleY(1);
      color: #95989a;
    }
  }

  .main-content {
    display: flex;
    flex-direction: column;
    gap: 15px;
    flex: 1 1 auto;
    min-height: 0;
    margin-inline-start: 40px;
    margin-top: 44.5px;
    margin-bottom: 40px;
    margin-inline-end: 50px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f8f9fa;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #389ceb;
      border-radius: 4px;

      &:hover {
        background: #2d8cd9;
      }
    }

    scrollbar-width: thin;
    scrollbar-color: #389ceb #f8f9fa;
  }

  .footer {
    display: flex;
    padding-inline-start: 40px;

    > div {
      margin: auto;
    }

    .confirm {
      margin-inline-start: 40px;
    }
  }

  &.compact {
    padding-bottom: 24px;
    box-sizing: border-box;

    &.small {
      min-width: 0;
      max-width: min(50vw, 480px);
      width: max(320px, 18vw);
    }

    .header {
      min-height: auto;
      align-items: flex-start;
      gap: 12px;
      padding: 20px 24px 12px;

      .text-container {
        flex: 1;
        min-width: 0;

        & > span {
          margin-inline-start: 0;
        }

        .primary-title {
          display: block;
          font-size: 20px;
          line-height: 1.35;
        }

        .secondary-title {
          margin-inline-start: 0;
          margin-top: 4px;
          display: block;
          font-size: 16px;
          line-height: 1.35;
        }
      }

      .close-btn {
        margin-inline-end: 0;
        flex-shrink: 0;
        line-height: 1;
        padding: 2px 4px;
      }
    }

    .main-content {
      margin: 0;
      padding: 4px 24px 16px;
      gap: 12px;
    }

    .footer {
      padding-inline: 24px;
      padding-bottom: env(safe-area-inset-bottom, 0);

      > div {
        width: 100%;
        margin: 0;
        display: flex;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 12px;
      }

      .confirm {
        margin-inline-start: 0;
      }
    }

    @media (max-width: 820px) {
      max-height: calc(100dvh - 32px - env(safe-area-inset-top, 0px));
      top: max(16px, env(safe-area-inset-top, 16px));
      transform: translateX(-50%);

      &.small,
      &.medium,
      &.large {
        width: calc(100vw - 32px);
        max-width: calc(100vw - 32px);
      }

      .header {
        padding: 16px 16px 10px;
        gap: 8px;

        .text-container .primary-title {
          font-size: 18px;
          line-height: 1.35;
        }
      }

      .main-content {
        padding: 4px 16px 12px;
      }

      .footer {
        padding-inline: 16px;

        > div {
          justify-content: stretch;
        }
      }
    }

    @media (max-width: 480px) {
      &.small,
      &.medium,
      &.large {
        width: calc(100vw - 16px);
        max-width: calc(100vw - 16px);
      }

      .header {
        padding: 14px 12px 8px;

        .text-container .primary-title {
          font-size: 16px;
        }
      }

      .main-content {
        padding: 4px 12px 12px;
      }

      .footer {
        padding-inline: 12px;
      }
    }
  }
}
</style>
